import { inject, Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
  HttpStatusCode,
} from '@angular/common/http';
import { Observable, throwError, catchError, from, switchMap } from 'rxjs';
import { KeycloakService } from 'keycloak-angular';
import { LockNavigationService } from 'src/app/shared/services/lock-navigation.service';
import { environment } from 'src/environments/environment';
import { SnackbarService } from 'src/app/shared/utils/snackbar/snackbar.service';

interface Profile extends Keycloak.KeycloakProfile {
  attributes: {
    company: string[];
    wasticsUserId: string[];
  };
}

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  keycloakService = inject(KeycloakService);
  snackbarService = inject(SnackbarService);
  lockNavigationService = inject(LockNavigationService);

  private handleAuthError(request: HttpErrorResponse): Observable<any> {
    console.error('[TokenInterceptor] Auth error handler:', {
      status: request.status,
      statusText: request.statusText,
      url: request.url,
      message: request.message,
      error: request.error,
    });

    if (
      request.status === HttpStatusCode.Unauthorized ||
      request.status === HttpStatusCode.Forbidden
    ) {
      console.warn('[TokenInterceptor] 401/403 received - logging out user');
      this.lockNavigationService.unlock();
      this.keycloakService.logout(window.location.toString());
    }
    if (
      request.status === HttpStatusCode.PreconditionRequired &&
      request.url.includes(environment.quarkusApiUrl)
    ) {
      console.log(
        '[TokenInterceptor] 412 Precondition Required - force token refresh'
      );
      this.keycloakService
        .getKeycloakInstance()
        .updateToken(-1)
        .then(() => {
          console.log('[TokenInterceptor] Token forcefully refreshed');
          this.keycloakService.loadUserProfile().then((userprofile) => {
            const { company, wasticsUserId } = (userprofile as Profile)
              .attributes;
            if (!(company && company[0] && wasticsUserId && wasticsUserId[0])) {
              console.warn(
                '[TokenInterceptor] User missing company or wasticsUserId attributes'
              );
              this.snackbarService.open({
                type: 'error',
                message:
                  'Bei diesem Modul ist ein Fehler aufgetreten. Versuchen Sie, die Seite neu zu laden, und wenn es dann immer noch nicht funktioniert, wenden Sie sich bitte an den Support.',
                duration: 10000,
              });
            }
          });
        })
        .catch((error) => {
          console.error('[TokenInterceptor] Force token refresh failed', error);
        });
    }
    return throwError(() => request);
  }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return from(this.keycloakService.updateToken(30)).pipe(
      switchMap((updated) => {
        if (updated) {
          // Token was refreshed, clone the request with the new token
          return from(this.keycloakService.getToken()).pipe(
            switchMap((token: string) => {
              const authReq = request.clone({
                setHeaders: {
                  Authorization: `Bearer ${token}`,
                },
              });
              return next
                .handle(authReq)
                .pipe(catchError((error) => this.handleAuthError(error)));
            })
          );
        } else {
          // Token is still valid, proceed without modifying the request
          return next.handle(request);
        }
      }),
      catchError((error) => {
        console.error('[TokenInterceptor] Token update failed', error);
        return this.handleAuthError(error);
      })
    );
  }
}
